import { fetchRouterBuild } from 'utils/robinhoodRouter/client'
import type { RouterErrorResponse, RouterExecutionData } from 'utils/robinhoodRouter/types'
import { validateBuildResponse } from 'utils/robinhoodRouter/validateBuild'
import { validateBuildCalldata } from 'utils/robinhoodRouter/validateCalldata'

// Duck-types the same shape `isRouterError` (utils/robinhoodRouter/client.ts) checks for. Inlined
// rather than imported so this helper's error handling doesn't depend on that specific export's
// runtime identity (e.g. under `jest.mock('utils/robinhoodRouter/client')` automocking in tests).
function isRouterErrorResponse(r: unknown): r is RouterErrorResponse {
  return !!r && typeof r === 'object' && 'error' in r && typeof (r as RouterErrorResponse).error?.code === 'string'
}

export interface RouterSwapTx {
  to: string
  data: string
  value: string
  gasLimit: string
}

export async function buildRouterSwapTransaction(
  routerData: RouterExecutionData,
  opts: { sender: string; recipient: string; slippageBps: number; deadlineSeconds: number }
): Promise<RouterSwapTx> {
  const result = await fetchRouterBuild({
    routeId: routerData.routeId,
    routeEnvelope: routerData.routeEnvelope,
    sender: opts.sender,
    recipient: opts.recipient,
    slippageBps: opts.slippageBps,
    deadlineSeconds: opts.deadlineSeconds,
  })
  if (isRouterErrorResponse(result)) {
    // The router already enforces min-out, deterioration, and simulation server-side; surface its reason.
    throw new Error(`Robinhood router build failed: ${result.error.message} [${result.error.code}]`)
  }

  // M4 audit finding 3 (High): the router is a trusted backend, but the client must still bind the
  // build response to the intent it accepted at quote time before ever handing it to sendTransaction.
  const validation = validateBuildResponse(result, routerData, opts.slippageBps)
  if (!validation.ok) {
    throw new Error(`Robinhood router build response failed validation: ${validation.reason}`)
  }

  // Prod-audit #1: metadata matching is not enough — decode the actual Universal Router calldata and
  // bind its inner commands (payer, recipient, amountIn, min-out, fee, path, deadline) to the accepted
  // swap, so a correct-selector/correct-metadata response with malicious inner commands is rejected.
  // Also bound the absolute deadline to the requested window so the router can't hand back a far-future
  // deadline (a stuck tx executing much later); the lower bound additionally rejects a deadline grossly
  // behind our clock. The allowance is a NARROW clock-skew/round-trip tolerance (it is skew-tolerant,
  // not a strict expiry check — on-chain block.timestamp is the real expiry gate), and it keeps a short
  // requested deadline from ballooning: a 30s request can extend to at most ~30s + allowance, not minutes.
  const DEADLINE_SKEW_SECONDS = 120
  const nowSec = Math.floor(Date.now() / 1000)
  const minDeadline = BigInt(nowSec - DEADLINE_SKEW_SECONDS)
  const maxDeadline = BigInt(nowSec + opts.deadlineSeconds + DEADLINE_SKEW_SECONDS)
  const calldata = validateBuildCalldata(result, routerData, opts.recipient, { minDeadline, maxDeadline })
  if (!calldata.ok) {
    throw new Error(`Robinhood router build calldata failed validation: ${calldata.reason}`)
  }

  return result.transaction
}
